import React, { useState } from "react";
import { FormData, Reseller } from "../../types";
import {
  Building,
  Check,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  FileText,
  User,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
interface OnboardingFlowProps {
  setCurrentView: (view: string) => void;
}

export const OnboardingFlow: React.FC<OnboardingFlowProps> = ({
  setCurrentView,
}) => {
  const [onboardingStep, setOnboardingStep] = useState(1);
    const userRole = localStorage.getItem("userRole");
  const [resellers, setResellers] = useState<Reseller[]>([]);
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    personalInfo: {},
    businessInfo: {},
    billingInfo: {},
    agreement: false,
  });
  const steps = [
    { id: 1, title: "Personal Information", icon: User },
    { id: 2, title: "Business Details", icon: Building },
    { id: 3, title: "Billing Setup", icon: CreditCard },
    { id: 4, title: "Agreement", icon: FileText },
  ];

 const handleNext = () => {
  if (onboardingStep < 4) {
    setOnboardingStep(onboardingStep + 1);
  } else {
    const newReseller = {
      id: resellers.length + 1,
      name: `${formData.personalInfo.firstName || "New"} ${
        formData.personalInfo.lastName || "User"
      }`,
      company: formData.businessInfo.companyName || "New Company",
      email: formData.personalInfo.email || "newuser@example.com",
      phone: formData.personalInfo.phone || "+1-555-0000",
      registrationDate: new Date().toISOString().split("T")[0],
      status: "New",
      totalSales: 0,
      commission: 0,
      territory: "Unassigned",
      level: "Bronze",
      lastLogin: new Date().toISOString().split("T")[0],
    };

    setResellers([...resellers, newReseller]);

    const userRole = localStorage.getItem("userRole");

    if (userRole === "global_admin") {
      navigate("/view-campaign");
    } else {
      navigate("/customer");
    }
  }
};

  const handlePrev = () => {
    if (onboardingStep > 1) {
      setOnboardingStep(onboardingStep - 1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className=" mb-8">
          <h1 className="text-3xl flex justify-between font-bold text-gray-900 mb-2">
            SMS Hub Reseller Registration
            <Link
              to={"/customer"}
              className="text-white text-center rounded-xl text-[14px] hover:underline h-[40px] w-14 ml-2 bg-blue-600"
            >
              Exit
            </Link>
          </h1>
          <p className="text-gray-600">
            Join our network and start earning with SMS services
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300 ${
                    onboardingStep >= step.id
                      ? "bg-blue-600 border-blue-600 text-white"
                      : "bg-white border-gray-300 text-gray-400"
                  }`}
                >
                  {onboardingStep > step.id ? (
                    <Check size={20} />
                  ) : (
                    <step.icon size={20} />
                  )}
                </div>
                <span
                  className={`ml-2 text-sm font-medium ${
                    onboardingStep >= step.id
                      ? "text-blue-600"
                      : "text-gray-400"
                  }`}
                >
                  {step.title}
                </span>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-4 ${
                      onboardingStep > step.id ? "bg-blue-600" : "bg-gray-300"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          {onboardingStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold mb-6">
                Personal Information
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter first name"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        personalInfo: {
                          ...formData.personalInfo,
                          firstName: e.target.value,
                        },
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter last name"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        personalInfo: {
                          ...formData.personalInfo,
                          lastName: e.target.value,
                        },
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter email"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        personalInfo: {
                          ...formData.personalInfo,
                          email: e.target.value,
                        },
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter phone number"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        personalInfo: {
                          ...formData.personalInfo,
                          phone: e.target.value,
                        },
                      })
                    }
                  />
                </div>
              </div>
            </div>
          )}

          {onboardingStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold mb-6">
                Business Information
              </h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter company name"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        businessInfo: {
                          ...formData.businessInfo,
                          companyName: e.target.value,
                        },
                      })
                    }
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Business Type
                    </label>
                    <select className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                      <option>Select business type</option>
                      <option>Individual</option>
                      <option>Partnership</option>
                      <option>Corporation</option>
                      <option>LLC</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Expected Monthly Volume
                    </label>
                    <select className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                      <option>Select volume</option>
                      <option>0 - 10K messages</option>
                      <option>10K - 100K messages</option>
                      <option>100K - 1M messages</option>
                      <option>1M+ messages</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Business Address
                  </label>
                  <textarea
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    placeholder="Enter complete business address"
                  ></textarea>
                </div>
              </div>
            </div>
          )}

          {onboardingStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold mb-6">Billing Setup</h2>
              <div className="space-y-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-blue-900 mb-2">
                    Initial Credit Purchase
                  </h3>
                  <p className="text-blue-700 text-sm">
                    Add initial credits to start sending SMS immediately
                  </p>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  {[
                    { amount: "₦100K", credits: "10,000", popular: false },
                    { amount: "₦250K", credits: "30,000", popular: true },
                    { amount: "₦500K", credits: "70,000", popular: false },
                  ].map((plan) => (
                    <div
                      key={plan.amount}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        plan.popular
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-blue-300"
                      }`}
                    >
                      {plan.popular && (
                        <span className="bg-blue-500 text-white px-2 py-1 text-xs rounded-full">
                          Popular
                        </span>
                      )}
                      <div className="text-center mt-2">
                        <div className="text-2xl font-bold">{plan.amount}</div>
                        <div className="text-gray-600">
                          {plan.credits} credits
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Payment Method
                    </label>
                    <select className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                      <option>Credit Card</option>
                      <option>Bank Transfer</option>
                      <option>PayPal</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Billing Cycle
                    </label>
                    <select className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                      <option>Prepaid</option>
                      <option>Monthly Billing</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {onboardingStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold mb-6">Terms & Agreement</h2>
              <div className="bg-gray-50 p-6 rounded-lg max-h-64 overflow-y-auto">
                <h3 className="font-semibold mb-3">
                  Reseller Agreement Summary
                </h3>
                <div className="text-sm text-gray-700 space-y-2">
                  <p>
                    • Commission Structure: Earn 15-25% commission on all SMS
                    credits sold
                  </p>
                  <p>
                    • Payment Terms: Monthly payments within 15 days of month
                    end
                  </p>
                  <p>
                    • Territory: Exclusive territory rights within your region
                  </p>
                  <p>
                    • Support: 24/7 technical support and dedicated account
                    manager
                  </p>
                  <p>
                    • Compliance: Adhere to SMS regulations and anti-spam
                    policies
                  </p>
                  <p>• Marketing: Access to co-branded marketing materials</p>
                </div>
              </div>
              <div className="space-y-4">
                <label className="flex items-center space-x-3">
                  <input type="checkbox" className="w-5 h-5 text-blue-600" />
                  <span className="text-sm">
                    I agree to the Reseller Terms and Conditions
                  </span>
                </label>
                <label className="flex items-center space-x-3">
                  <input type="checkbox" className="w-5 h-5 text-blue-600" />
                  <span className="text-sm">
                    I agree to comply with SMS regulations and anti-spam
                    policies
                  </span>
                </label>
                <label className="flex items-center space-x-3">
                  <input type="checkbox" className="w-5 h-5 text-blue-600" />
                  <span className="text-sm">
                    I agree to receive marketing communications about platform
                    updates
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            <button
              onClick={handlePrev}
              disabled={onboardingStep === 1}
              className="flex items-center px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={20} className="mr-2" />
              Previous
            </button>
            <button
              onClick={handleNext}
              className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {onboardingStep === 4 ? "Complete Registration" : "Next"}
              <ChevronRight size={20} className="ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
